/**
 * Settings: economy switches (commission, deposit, sell, withdrawal), shroom
 * pricing, and the company info block that is printed on invoices and shown in
 * the footer.
 */
import { el, toast } from '../../dom';
import { adminApi, type AdminSettings, type CompanyInfo } from '../../game-api';
import { validateSettings } from './format';
import {
  btn, checkbox, fieldError, input, labelled, loadInto, panel, setFieldError, toastError,
} from './ui';

const COMPANY_FIELDS: { key: keyof CompanyInfo; label: string }[] = [
  { key: 'name', label: 'Company name' },
  { key: 'legalName', label: 'Legal name' },
  { key: 'regNumber', label: 'Registration number' },
  { key: 'vatNumber', label: 'VAT number' },
  { key: 'address', label: 'Address' },
  { key: 'country', label: 'Country' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
];

/** One line under a switch that says, in words, what its current position means. */
function statusLine(box: HTMLInputElement, onText: string, offText: string): HTMLElement {
  const line = el('div', 'text-[11px] text-text-dim mt-1', { dataset: { status: '' } });
  const sync = () => { line.textContent = box.checked ? onText : offText; };
  box.addEventListener('change', sync);
  sync();
  return line;
}

export function renderSettings(host: HTMLElement): () => void {
  return loadInto(host, () => adminApi.settings(), (s, reload) => view(s, reload), "Couldn't load settings.");
}

function view(s: AdminSettings, reload: () => void): HTMLElement {
  const commission = input('0', String(s.commissionPercent ?? 0), 'number');
  const commissionErr = fieldError();
  const crystals = input('0', String(s.cryptePerEur ?? 0), 'number');
  const crystalsErr = fieldError();
  const minWithdrawal = input('0', String(s.minWithdrawalEur ?? 0), 'number');
  const minWithdrawalErr = fieldError();

  const deposit = checkbox('Deposits enabled', !!s.depositEnabled);
  const sell = checkbox('Selling enabled', !!s.sellEnabled);
  const withdrawal = checkbox('Withdrawals enabled', !!s.withdrawalEnabled);
  const kyc = checkbox('Require identity verification (KYC) to withdraw', !!s.kycEnabled);
  const invoices = checkbox('Issue an invoice for every approved withdrawal', !!s.invoicesEnabled);
  const invoiceEmail = checkbox('E-mail invoices to players', s.invoiceEmailEnabled !== false);
  const depositNote = statusLine(deposit.input,
    'Deposits are ON — players can buy Shrooms.', 'Deposits are OFF — players cannot buy Shrooms.');
  const sellNote = statusLine(sell.input,
    'Selling is ON — players can sell Shrooms back.', 'Selling is OFF.');
  const invoiceEmailNote = statusLine(invoiceEmail.input,
    'Invoice e-mails are ON — every invoice is sent to the player (never to admin-created players).',
    'Invoice e-mails are OFF — invoices are still issued and downloadable, but not e-mailed.');
  const withdrawalNote = el('div', 'text-[11px] text-text-dim mt-1');
  const syncWithdrawalNote = () => {
    const on = withdrawal.input.checked;
    // Three switches, one sentence: an operator should be able to read the exact
    // state of cash-out off this line without opening the Payouts tab.
    withdrawalNote.textContent = on
      ? (kyc.input.checked
        ? 'Players can request withdrawals once their identity check is approved.'
        : 'Players can request withdrawals — WITHOUT any identity check.')
      : 'Withdrawals are currently OFF — players cannot cash out.';
    withdrawalNote.textContent += ' Card and SEPA payouts are switched one by one on the Payments tab.';
    // KYC and invoices only ever act on a withdrawal, so they are inert while
    // cash-out is closed. Shown as disabled rather than hidden: the operator is
    // configuring what happens when they open it.
    for (const c of [kyc, invoices]) {
      c.input.disabled = !on;
      c.wrap.classList.toggle('opacity-50', !on);
    }
  };
  withdrawal.input.addEventListener('change', syncWithdrawalNote);
  kyc.input.addEventListener('change', syncWithdrawalNote);
  syncWithdrawalNote();

  const company = s.company ?? ({} as CompanyInfo);
  const companyInputs = new Map<keyof CompanyInfo, HTMLInputElement>();
  const companyGrid = el('div', 'grid gap-3', { style: 'grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));' });
  for (const f of COMPANY_FIELDS) {
    const inp = input(f.label, String(company[f.key] ?? ''));
    companyInputs.set(f.key, inp);
    companyGrid.appendChild(labelled(f.label, inp));
  }

  const save = btn('Save settings', async () => {
    const next: AdminSettings = {
      commissionPercent: Number(commission.value),
      cryptePerEur: Number(crystals.value),
      minWithdrawalEur: Number(minWithdrawal.value),
      depositEnabled: deposit.input.checked,
      sellEnabled: sell.input.checked,
      withdrawalEnabled: withdrawal.input.checked,
      kycEnabled: kyc.input.checked,
      invoicesEnabled: invoices.input.checked,
      invoiceEmailEnabled: invoiceEmail.input.checked,
      company: Object.fromEntries(
        COMPANY_FIELDS.map((f) => [f.key, companyInputs.get(f.key)!.value.trim()]),
      ) as unknown as CompanyInfo,
    };
    const errors = validateSettings(next);
    setFieldError(commissionErr, errors.commissionPercent);
    setFieldError(crystalsErr, errors.cryptePerEur);
    setFieldError(minWithdrawalErr, errors.minWithdrawalEur);
    if (Object.keys(errors).length) return;
    try {
      await adminApi.saveSettings(next);
      toast('Settings saved');
      reload();
    } catch (e) { toastError(e, 'Could not save settings'); }
  }, 'primary');

  return el('div', 'flex flex-col gap-4', {},
    panel('Economy',
      el('div', 'grid gap-3', { style: 'grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));' },
        el('div', '', {}, labelled('Commission %', commission), commissionErr),
        el('div', '', {}, labelled('Shrooms per EUR', crystals), crystalsErr),
        el('div', '', {}, labelled('Minimum withdrawal (EUR)', minWithdrawal), minWithdrawalErr)),
      el('div', 'flex flex-col gap-2 mt-4 pt-3 border-t border-border/60', {},
        deposit.wrap, depositNote, sell.wrap, sellNote, withdrawal.wrap, withdrawalNote,
        el('div', 'flex flex-col gap-2 pl-5 mt-1', {}, kyc.wrap, invoices.wrap))),
    panel('Invoices',
      el('div', 'flex flex-col gap-2', {}, invoiceEmail.wrap, invoiceEmailNote)),
    panel('Company info',
      el('div', 'text-xs text-text-dim mb-3', {},
        'These details appear on invoices and in the site footer.'),
      companyGrid),
    el('div', 'flex justify-end', {}, save));
}
